import { useCallback, useEffect, useRef, useState } from 'react';

const LINE_HEIGHT = 1.6;
const FONT_SIZE = 14;
const LINE_PX = FONT_SIZE * LINE_HEIGHT; // 22.4px
const MAX_LINES = 4;
const COLLAPSED_MAX_HEIGHT = Math.floor(LINE_PX * MAX_LINES); // 89px — exactly 4 lines

interface EditableDescriptionProps {
  description: string;
  onSave: (newDescription: string) => Promise<void>;
}

export default function EditableDescription({ description, onSave }: EditableDescriptionProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(description);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(description);
  }, [description]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editing]);

  // Detect if the text overflows the collapsed height.
  // Recompute on description changes and container resizes.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > COLLAPSED_MAX_HEIGHT);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [description, editing]);

  const save = useCallback(async () => {
    if (draft === description) {
      setEditing(false);
      setError(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setSaving(false);
      setEditing(false);
    } catch (err) {
      setSaving(false);
      setError(err instanceof Error ? err.message : 'Failed to save description');
    }
  }, [draft, description, onSave]);

  const cancel = useCallback(() => {
    setDraft(description);
    setEditing(false);
    setError(null);
  }, [description]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
      // Note: Enter does NOT save — this is a textarea supporting multiline.
    },
    [cancel],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (containerRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }
      save();
    },
    [save],
  );

  // Edit mode
  if (editing) {
    return (
      <div ref={containerRef} style={{ marginTop: '8px' }} onBlur={handleBlur}>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={saving}
          rows={6}
          style={{
            width: '100%',
            maxWidth: '800px',
            fontSize: `${FONT_SIZE}px`,
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
            lineHeight: LINE_HEIGHT,
          }}
        />
        {error && (
          <div style={{ color: '#d1242f', fontSize: '13px', marginTop: '4px' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
          <button
            type="button"
            className="btn-small"
            disabled={saving}
            onClick={save}
          >
            Save
          </button>
          <button
            type="button"
            className="btn-small btn-secondary"
            disabled={saving}
            onClick={cancel}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // View mode
  if (!description) {
    return (
      <div style={{ marginTop: '8px' }}>
        <span
          role="button"
          tabIndex={0}
          onClick={() => setEditing(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setEditing(true);
            }
          }}
          style={{
            cursor: 'pointer',
            color: '#656d76',
            fontStyle: 'italic',
            fontSize: `${FONT_SIZE}px`,
          }}
        >
          Click to add a description
        </span>
      </div>
    );
  }

  const isCollapsed = overflows && !expanded;

  return (
    <div style={{ marginTop: '8px', maxWidth: '800px' }}>
      <div
        ref={textRef}
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setEditing(true);
          }
        }}
        style={{
          fontSize: `${FONT_SIZE}px`,
          lineHeight: LINE_HEIGHT,
          cursor: 'pointer',
          whiteSpace: 'pre-wrap',
          maxHeight: expanded ? 'none' : `${COLLAPSED_MAX_HEIGHT}px`,
          overflow: 'hidden',
        }}
      >
        {description}
      </div>
      {isCollapsed && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '2px',
          }}
        >
          <span style={{ color: '#656d76', fontSize: `${FONT_SIZE}px` }}>...</span>
          <a
            href="#"
            tabIndex={0}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(true); }}
            style={{
              fontSize: `${FONT_SIZE}px`,
              color: '#0969da',
              textDecoration: 'underline',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Show More
          </a>
        </div>
      )}
      {overflows && expanded && (
        <a
          href="#"
          tabIndex={0}
          onClick={(e) => { e.preventDefault(); setExpanded(false); }}
          style={{
            fontSize: `${FONT_SIZE}px`,
            display: 'inline-block',
            marginTop: '4px',
            color: '#0969da',
            textDecoration: 'underline',
            fontWeight: 500,
          }}
        >
          Show Less
        </a>
      )}
    </div>
  );
}
